// Where stretch jobs run: a module Worker in the browser, or inline where
// there is none (vitest, a very old browser). The inline path returns the
// same results, only on the calling thread.

import { detectOnsets, type Onset } from "@web-shared/lib/stretch/onsets";
import { renderWarped, type RenderSpec } from "@web-shared/lib/stretch/render";
import type { StretchEngineName, StretchRequest, StretchResponse } from "@web-shared/lib/stretch/protocol";
import type { StretchedPcm } from "@web-shared/lib/warp";
import type { PcmSource } from "@web-shared/lib/wav";

/** A newer render for the same lane replaced this one before it ran. */
export class Superseded extends Error {
  constructor() {
    super("superseded");
  }
}

export interface StretchRenderer {
  /** Render `source` (the object behind `url`) per `spec`. A newer render
   * for the same `lane` may make this one reject with `Superseded`. */
  render(url: string, source: PcmSource, spec: RenderSpec, lane: string): Promise<StretchedPcm>;
  /** The object's transients, computed once per url. */
  onsets(url: string, source: PcmSource): Promise<Onset[]>;
  /** Drop what is held for `url` (the object was evicted). */
  forget(url: string): void;
  /** Which engine renders: the wasm build, or the TypeScript vocoder. */
  engine(): Promise<StretchEngineName>;
}

function pcmOf(sampleRate: number, channels: Float32Array<ArrayBuffer>[]): StretchedPcm {
  return {
    sampleRate,
    numberOfChannels: channels.length,
    length: channels[0]?.length ?? 0,
    channels,
    getChannelData: (c) => channels[c] ?? channels[0]!,
  };
}

/** Runs every job on the calling thread. */
export function inlineRenderer(): StretchRenderer {
  const found = new Map<string, Onset[]>();
  const onsetsOf = (url: string, source: PcmSource): Onset[] => {
    const hit = found.get(url);
    if (hit) return hit;
    const o = detectOnsets(source);
    found.set(url, o);
    return o;
  };
  return {
    render: async (url, source, spec) => renderWarped(source, { ...spec, onsets: spec.onsets ?? onsetsOf(url, source) }),
    onsets: async (url, source) => onsetsOf(url, source),
    forget: (url) => void found.delete(url),
    engine: async () => "typescript",
  };
}

/** Runs jobs on a Worker; null when this environment has none. */
export function workerRenderer(): StretchRenderer | null {
  if (typeof Worker === "undefined") return null;
  let worker: Worker;
  try {
    worker = new Worker(new URL("./stretch.worker.ts", import.meta.url), { type: "module" });
  } catch {
    return null;
  }
  const loaded = new Set<string>();
  const pending = new Map<number, { resolve: (v: StretchResponse) => void; reject: (e: Error) => void }>();
  let nextId = 1;
  worker.onmessage = (e: MessageEvent<StretchResponse>) => {
    const p = pending.get(e.data.id);
    if (!p) return;
    pending.delete(e.data.id);
    if (e.data.type === "superseded") p.reject(new Superseded());
    else if (e.data.type === "failed") p.reject(new Error(e.data.message));
    else p.resolve(e.data);
  };
  worker.onerror = (e) => {
    for (const p of pending.values()) p.reject(new Error(e.message || "stretch worker failed"));
    pending.clear();
  };
  const post = (msg: StretchRequest): void => worker.postMessage(msg);
  const ensure = (url: string, source: PcmSource): void => {
    if (loaded.has(url)) return;
    // Copies, not transfers: the channels belong to a live AudioBuffer.
    const channels = Array.from({ length: source.numberOfChannels }, (_, c) => source.getChannelData(c).slice());
    post({ type: "load", url, source: { sampleRate: source.sampleRate, channels } });
    loaded.add(url);
  };
  const ask = (build: (id: number) => StretchRequest): Promise<StretchResponse> =>
    new Promise((resolve, reject) => {
      const id = nextId++;
      pending.set(id, { resolve, reject });
      post(build(id));
    });
  return {
    render: async (url, source, spec, lane) => {
      ensure(url, source);
      const r = await ask((id) => ({ type: "render", id, url, lane, spec }));
      if (r.type !== "rendered") throw new Error("unexpected stretch response");
      return pcmOf(r.sampleRate, r.channels);
    },
    onsets: async (url, source) => {
      ensure(url, source);
      const r = await ask((id) => ({ type: "onsets", id, url }));
      if (r.type !== "onsets") throw new Error("unexpected stretch response");
      return r.onsets;
    },
    forget: (url) => {
      if (!loaded.delete(url)) return;
      post({ type: "forget", url });
    },
    engine: async () => {
      const r = await ask((id) => ({ type: "engine", id }));
      if (r.type !== "engine") throw new Error("unexpected stretch response");
      return r.name;
    },
  };
}

/** The Worker where there is one, inline otherwise. */
export function defaultRenderer(): StretchRenderer {
  return workerRenderer() ?? inlineRenderer();
}
